import React, { useEffect, useState } from 'react';
import { formatDate } from 'components/DateTime';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Button,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
  Box,
  Paper,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';

import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import PagedTable from 'components/table/PagedTable';
import CheckIcon from '@mui/icons-material/Check';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import CommentIcon from '@mui/icons-material/Comment';
import LoginIcon from '@mui/icons-material/Login';

import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { showValidationAlert } from 'helper/form';
import { userSelector } from 'helper/security';
import { openLinkInNewTab } from 'helper/link';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputCountryDropdown, FormInputDropdown, SimpleDropdown, FormInputSubdivisionDropdown } from 'components/form/FormInputDropdown';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import CustomTabs from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { SpaceSelectionInput, buildSpaceSelectionOptions } from 'components/security/SpaceSelectionInput';
import { canEditAdminRecord, canEditAdminSpaceId, initialSpace } from 'helper/security';
import { filterSelector } from 'helper/filter';

import ProductDetailsFlat, {
  ProductDetailsFlatContentSchema,
  ProductDetailsFlatFromTypeToSchema,
  ProductDetailsFlatFromSchemaToType,
} from '../content/products/productdetailsflat_embedded';
import { AvailabilityEmbeddedTable } from '../availability/availability';

import {
  PUBLIC_COMPANY_LIST_QUERY,
  UPDATE_PUBLIC_CLIENT_MUTATION,
  UPDATE_PUBLIC_COMPANY_MUTATION,
  CREATE_PUBLIC_COMPANY_MUTATION,
  VIEW_PUBLIC_CLIENT_QUERY,
  VIEW_PUBLIC_COMPANY_QUERY,
  LIST_KEY_ACCOUNT_MANAGERS_QUERY,
  PUBLIC_CLIENT_LIST_QUERY,
  DEACTIVATE_PUBLIC_CLIENT_MUTATION,
  REACTIVATE_PUBLIC_CLIENT_MUTATION,
  DEACTIVATE_PUBLIC_COMPANY_MUTATION,
  REACTIVATE_PUBLIC_COMPANY_MUTATION,
  RESEND_COMPANY_INVITATION_MUTATION,
  LIST_ACCOUNTING_AREAS,
  LIST_USER_GROUPS_QUERY,
  SUDO_CLIENT_MUTATION,
} from './gql';
import { TAXTYPES_LIST_FULL_QUERY } from '../prices/gql';
import {
  TaxTypeListOutput,
  ProductListOutput,
  EProductPayment,
  EProductLookupCode,
  ProductComponentListOutput,
  HL_PublicClientOutput,
  HL_PublicCompanyOutput,
  ECountryCode,
} from '__generated__/graphql';
import yup from 'validation';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { ISpace } from 'types';
import { TaxComponentSelectionInput } from 'components/tax/TaxComponentSelectionInput';
import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import { isProductionRelease } from 'helper/deployment';
import { add } from 'lodash';
import { getPhoneCountryOptions } from 'semshared/utils/phoneCountryCodes';
import { getDialCodeForCountry } from 'semshared/utils/dialCodes';
import { CompanyCommentsSection } from 'components/comments';
import CompanyContractsTab from '../contracts/CompanyContractsTab';
import AccountingAreasComponent from '../../components/AccountingAreas';
import { UserGroupsSection } from '../../components/usergroups';
import CompanyTasksList from '../../components/CompanyTasksList';

// Helper function to check if invitation is pending
const isInvitationPending = (client: any) => {
  return !client.emailVerified && client.invitationToken && client.invitationTokenExpiry && new Date(client.invitationTokenExpiry) > new Date();
};

interface PublicCompanyProps {
  id: number;
}
interface PublicCompanyFormProps {
  data: HL_PublicCompanyOutput;
}

function PublicCompanyForm(props: PublicCompanyFormProps) {
  const validationSchema = yup.object().shape({
    id: yup.number().required(),
    name: yup.string().required().label(i18next.t('publiccompany-name')),
    mainLanguage: yup.string().default('DE').label(i18next.t('publiccompany-main-language')),
    address: yup.string().label(i18next.t('publiccompany-street')),
    zip: yup.string().label(i18next.t('publiccompany-zip')),
    city: yup.string().label(i18next.t('publiccompany-city')),
    country: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).label(i18next.t('publiccompany-country')),
    state: yup.string().label(i18next.t('publiccompany-state')),
    addressSupplement: yup.string().label(i18next.t('publiccompany-address-supplement')),
    phoneCountryCode: yup.string().label(i18next.t('publiccompany-phone-country-code')),
    phone: yup.string().label(i18next.t('publiccompany-phone-number')),
    email: yup.string().email().label(i18next.t('publiccompany-email')),
    industry: yup.string().label(i18next.t('publiccompany-industry')),
    website: yup.string().url().label(i18next.t('publiccompany-website')),
    keyAccountManagerId: yup.number().nullable().label(i18next.t('publiccompany-key-account-manager')),
    isPremium: yup.boolean().required().label(i18next.t('publiccompany-premium')),
    showPricesInNet: yup.boolean().required().label(i18next.t('publiccompany-show-prices-net')),
    privacyPolicyAccepted: yup.boolean().required().label(i18next.t('publiccompany-privacy-policy-accepted')),
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = userSelector()!;
  const isNew = props.data.id === 0;
  const isCompanyActive = props.data.isActive !== false;

  const [updateMutateFunction] = useMutation(UPDATE_PUBLIC_COMPANY_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_PUBLIC_COMPANY_MUTATION);
  const [deactivateCompanyMutation] = useMutation(DEACTIVATE_PUBLIC_COMPANY_MUTATION);
  const [reactivateCompanyMutation] = useMutation(REACTIVATE_PUBLIC_COMPANY_MUTATION);
  const [deactivateClientMutation] = useMutation(DEACTIVATE_PUBLIC_CLIENT_MUTATION, {
    refetchQueries: [{ query: LIST_USER_GROUPS_QUERY, variables: { where: { publicCompanyId: props.data.id } } }],
  });
  const [reactivateClientMutation] = useMutation(REACTIVATE_PUBLIC_CLIENT_MUTATION, {
    refetchQueries: [{ query: LIST_USER_GROUPS_QUERY, variables: { where: { publicCompanyId: props.data.id } } }],
  });
  const [sudoClientMutation] = useMutation(SUDO_CLIENT_MUTATION);
  const [resendInvitationMutation] = useMutation(RESEND_COMPANY_INVITATION_MUTATION);
  const { data: usersData } = useQuery(LIST_KEY_ACCOUNT_MANAGERS_QUERY);
  const { data: accountingAreasData, loading: accountingAreasLoading } = useQuery(LIST_ACCOUNTING_AREAS, {
    variables: { publicCompanyId: props.data.id },
    skip: props.data.id === 0,
  });
  const {
    data: activeClientsData,
    loading: activeClientsLoading,
    refetch: refetchActiveClients,
  } = useQuery(PUBLIC_CLIENT_LIST_QUERY, {
    variables: {
      where: { publicCompanyId: props.data.id, isActive: true },
    },
    skip: props.data.id === 0,
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: inactiveClientsData,
    loading: inactiveClientsLoading,
    refetch: refetchInactiveClients,
  } = useQuery(PUBLIC_CLIENT_LIST_QUERY, {
    variables: {
      where: { publicCompanyId: props.data.id, isActive: false },
    },
    skip: props.data.id === 0,
    fetchPolicy: 'cache-and-network',
  });
  // State für den Buchungen-Dialog
  const [showBookingsDialog, setShowBookingsDialog] = useState(false);
  const [blockedDialogType, setBlockedDialogType] = useState<'client' | 'company'>('client');
  const [blockedClientName, setBlockedClientName] = useState('');
  const [blockingReason, setBlockingReason] = useState('');
  const [blockingBookings, setBlockingBookings] = useState<any[]>([]);

  type PublicCompanyFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: Partial<HL_PublicCompanyOutput>): PublicCompanyFormType => ({
    id: obj.id || 0,
    name: obj.name || '',
    mainLanguage: obj.mainLanguage || 'DE',
    address: obj.address || '',
    zip: obj.zip || '',
    city: obj.city || '',
    country: obj.country || ECountryCode.DE,
    state: obj.state || '',
    addressSupplement: obj.addressSupplement || '',
    phoneCountryCode: obj.phoneCountryCode || '+49',
    phone: obj.phone || '',
    email: obj.email || '',
    industry: obj.industry || '',
    website: obj.website || '',
    keyAccountManagerId: obj.keyAccountManagerId || null,
    isPremium: obj.isPremium || false,
    showPricesInNet: obj.showPricesInNet ?? true,
    privacyPolicyAccepted: obj.privacyPolicyAccepted || false,
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    watch,
    setValue,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client: useApolloClient() },
    defaultValues: toFormSchema((props.data || {}) as PublicCompanyFormType),
  });


  // Watch for country changes
  const watchCountry = watch('country');

  // Auto-assign phone country code based on selected country
  useEffect(() => {
    if (watchCountry) {
      const dialCode = getDialCodeForCountry(watchCountry);
      if (dialCode) {
        setValue('phoneCountryCode', dialCode);
      }
    }
  }, [watchCountry, setValue]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  const onSubmit = async (values: PublicCompanyFormType) => {
    try {
      const companyData = {
        name: values.name,
        mainLanguage: values.mainLanguage,
        address: values.address,
        zip: values.zip,
        city: values.city,
        country: values.country,
        state: values.state,
        addressSupplement: values.addressSupplement,
        phoneCountryCode: values.phoneCountryCode,
        phone: values.phone,
        email: values.email,
        industry: values.industry,
        website: values.website,
        keyAccountManagerId: values.keyAccountManagerId,
        isPremium: values.isPremium,
        showPricesInNet: values.showPricesInNet,
      };

      if (isNew) {
        const res = await createMutateFunction({
          variables: {
            data: companyData,
          },
          update: cache => {
            // Invalidate the companies list cache to force refetch
            cache.evict({
              fieldName: 'listPublicCompanies',
            });
            cache.gc();
          },
        });
        if (res.data?.createPublicCompany?.id) {
          dispatchMessage(dispatch, i18next.t('publiccompany-created'));
          // Navigate directly to the new company detail page
          navigate(`/clients/companies/${res.data.createPublicCompany.id}/basic-data`);
          return; // Exit early to avoid double message
        }
      } else {
        const result = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: companyData,
          },
          update: cache => {
            // Invalidate the companies list cache to force refetch
            cache.evict({
              fieldName: 'listPublicCompanies',
            });
            cache.gc();
          },
          refetchQueries: ['ViewPublicCompany'],
          awaitRefetchQueries: true,
        });
        // Reset form with updated data after successful save
        if (result.data?.updatePublicCompany) {
          reset(toFormSchema(result.data.updatePublicCompany as Partial<HL_PublicCompanyOutput>));
        }
      }
      dispatchMessage(dispatch, isNew ? i18next.t('publiccompany-created') : i18next.t('publiccompany-updated'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const handleDeactivateClient = async (clientId: number, clientName: string) => {
    try {
      await deactivateClientMutation({
        variables: { id: clientId },
        update: cache => {
          // Evict all client list queries from cache to force refetch
          cache.evict({ fieldName: 'listPublicClients' });
          cache.gc();
        },
      });

      // Force refresh of both tables
      setTimeout(() => {
        refetchActiveClients();
        refetchInactiveClients();
      }, 100);

      dispatchMessage(dispatch, i18next.t('publicclient-deactivated-success'));
    } catch (err: any) {
      console.log('Deactivation error:', err);

      // Handle specific error codes from the backend
      const errorMessage = err.graphQLErrors?.[0]?.message || err.message;
      console.log('Error message:', errorMessage);

      // Try to parse the error message as JSON to check if it contains booking details
      try {
        const errorData = JSON.parse(errorMessage);
        console.log('Parsed error data:', errorData);

        if (errorData.code === 'PENDING_OFFERS_EXIST' || errorData.code === 'RESERVED_OFFERS_EXIST' || errorData.code === 'INVOICE_OFFERS_EXIST') {
          console.log('Showing booking dialog with bookings:', errorData.bookings);
          setBlockedDialogType('client');
          setBlockedClientName(clientName);
          setBlockingReason(errorData.message);
          setBlockingBookings(errorData.bookings || []);
          setShowBookingsDialog(true);
          return;
        }
      } catch (parseError) {
        console.log('JSON parse error:', parseError);
        // Not a JSON error, continue with regular error handling
      }

      // Fallback to generic error handling
      console.log('Using fallback error handling');
      dispatchException(dispatch, err);
    }
  };

  const handleDeactivateCompany = async () => {
    try {
      await deactivateCompanyMutation({
        variables: { id: props.data.id },
        update: cache => {
          cache.evict({ fieldName: 'listPublicCompanies' });
          cache.evict({ fieldName: 'listPublicClients' });
          cache.gc();
        },
        refetchQueries: ['ViewPublicCompany'],
        awaitRefetchQueries: true,
      });

      refetchActiveClients();
      refetchInactiveClients();
      dispatchMessage(dispatch, i18next.t('publiccompany-deactivated-success'));
    } catch (err: any) {
      const errorMessage = err.graphQLErrors?.[0]?.message || err.message;

      try {
        const errorData = JSON.parse(errorMessage);
        if (errorData.code === 'COMPANY_ACTIVE_BOOKINGS_EXIST') {
          setBlockedDialogType('company');
          setBlockedClientName('');
          setBlockingReason(errorData.message);
          setBlockingBookings(errorData.bookings || []);
          setShowBookingsDialog(true);
          return;
        }
      } catch (parseError) {
        // Not a JSON error, continue with regular error handling.
      }

      dispatchException(dispatch, err);
    }
  };

  const handleReactivateCompany = async () => {
    try {
      await reactivateCompanyMutation({
        variables: { id: props.data.id },
        update: cache => {
          cache.evict({ fieldName: 'listPublicCompanies' });
          cache.gc();
        },
        refetchQueries: ['ViewPublicCompany'],
        awaitRefetchQueries: true,
      });

      dispatchMessage(dispatch, i18next.t('publiccompany-reactivated-success'));
    } catch (err: any) {
      dispatchException(dispatch, err);
    }
  };

  const handleResendInvitation = async (clientId: number, clientName: string) => {
    try {
      await resendInvitationMutation({
        variables: { clientId },
      });
      dispatchMessage(dispatch, String(i18next.t('publicclient-resend-invitation-success', { clientName })));
    } catch (err: any) {
      dispatchException(dispatch, err);
    }
  };

  const handleReactivateClient = async (clientId: number, clientName: string) => {
    if (!isCompanyActive) {
      dispatchException(dispatch, i18next.t('publiccompany-inactive-contact-action-blocked'));
      return;
    }

    try {
      await reactivateClientMutation({
        variables: { id: clientId },
        update: cache => {
          // Evict all client list queries from cache to force refetch
          cache.evict({ fieldName: 'listPublicClients' });
          cache.gc();
        },
      });

      // Force refresh of both tables
      setTimeout(() => {
        refetchActiveClients();
        refetchInactiveClients();
      }, 100);

      dispatchMessage(dispatch, i18next.t('publicclient-reactivated-success'));
    } catch (err: any) {
      dispatchException(dispatch, err);
    }
  };

  // Basisdaten Tab Content
  const basicDataTab = (
    <Grid container spacing={3}>
      <UnsavedChangesPrompt isDirty={isDirty && !isNew} />

      {/* Grundinformationen */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BusinessIcon sx={{ mr: 1 }} />
            {i18next.t('publiccompany-section-basic')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormInputText name="name" control={control} label={`${i18next.t('publiccompany-name')} *`} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputDropdown
                name="mainLanguage"
                control={control}
                label={i18next.t('publiccompany-main-language')}
                options={[
                  { value: 'DE', label: i18next.t('publiccompany-language-german') },
                  { value: 'EN', label: i18next.t('publiccompany-language-english') },
                ]}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Adresse */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LocationOnIcon sx={{ mr: 1 }} />
            {i18next.t('publiccompany-section-address')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <FormInputText name="address" control={control} label={i18next.t('publiccompany-street')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInputText name="addressSupplement" control={control} label={i18next.t('publiccompany-address-supplement')} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormInputText name="zip" control={control} label={i18next.t('publiccompany-zip')} />
            </Grid>
            <Grid item xs={12} sm={9}>
              <FormInputText name="city" control={control} label={i18next.t('publiccompany-city')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputCountryDropdown name="country" control={control} label={i18next.t('publiccompany-country')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputSubdivisionDropdown name="state" control={control} label={i18next.t('publiccompany-state')} countryFieldName="country" />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Kontakt */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ContactMailIcon sx={{ mr: 1 }} />
            {i18next.t('publiccompany-section-contact')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {i18next.t('publiccompany-section-phone')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInputDropdown
                name="phoneCountryCode"
                control={control}
                label={i18next.t('publiccompany-phone-country-code')}
                options={getPhoneCountryOptions(i18next.language || 'en')}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormInputText name="phone" control={control} label={i18next.t('publiccompany-phone-number')} />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputText name="email" control={control} label={i18next.t('publiccompany-email')} textFieldProps={{ type: 'email' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputText name="website" control={control} label={i18next.t('publiccompany-website')} textFieldProps={{ type: 'url' }} />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Geschäftsinformationen */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SettingsIcon sx={{ mr: 1 }} />
            {i18next.t('publiccompany-section-business')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormInputText name="industry" control={control} label={i18next.t('publiccompany-industry')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormInputDropdown
                name="keyAccountManagerId"
                control={control}
                label={i18next.t('publiccompany-key-account-manager')}
                options={[
                  { value: null, label: i18next.t('publiccompany-no-selection') },
                  ...(usersData?.listKeyAccountManagers?.map(user => ({
                    value: user.id,
                    label: `${user.name} (${user.email})`,
                  })) || []),
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInputCheckbox name="isPremium" control={control} label={i18next.t('publiccompany-premium')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInputCheckbox name="showPricesInNet" control={control} label={i18next.t('publiccompany-show-prices-net')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInputCheckbox name="privacyPolicyAccepted" control={control} label={i18next.t('publiccompany-privacy-policy-accepted')} disabled />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Kommentare */}
      {!isNew && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CommentIcon sx={{ mr: 1 }} />
              {i18next.t('publiccompany-section-comments')}
            </Typography>
            <CompanyCommentsSection companyId={props.data.id} companyName={props.data.name || ''} />
          </Paper>
        </Grid>
      )}

      {/* Tasks */}
      {!isNew && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CommentIcon sx={{ mr: 1 }} />
              {i18next.t('publiccompany-section-tasks')}
            </Typography>
            <CompanyTasksList publicCompanyId={props.data.id} />
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <Button
          sx={{ marginRight: 2 }}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating}
          onClick={async () => {
            const valid = await trigger();
            if (valid) {
              handleSubmit(onSubmit)();
            }
          }}
        >
          {i18next.t('publiccompany-button-save')}
        </Button>
        {!isNew && isCompanyActive && (
          <ConfirmationButton
            confirmationTitle={i18next.t('publiccompany-deactivate-title')}
            confirmationQuestion={i18next.t('publiccompany-deactivate-question')}
            onConfirm={handleDeactivateCompany}
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
          >
            {i18next.t('publiccompany-deactivate-button')}
          </ConfirmationButton>
        )}
        {!isNew && !isCompanyActive && (
          <ConfirmationButton
            confirmationTitle={i18next.t('publiccompany-reactivate-title')}
            confirmationQuestion={i18next.t('publiccompany-reactivate-question')}
            onConfirm={handleReactivateCompany}
            variant="outlined"
            color="success"
            startIcon={<RestoreIcon />}
          >
            {i18next.t('publiccompany-reactivate-button')}
          </ConfirmationButton>
        )}
      </Grid>
    </Grid>
  );

  // Contracts Tab Content
  const contractsTab = <CompanyContractsTab publicCompanyId={props.data.id} canEdit={props.data.id > 0} />;

  // Contacts Tab Content
  const contactsTab = (
    <Grid container spacing={3}>
      {!isCompanyActive && (
        <Grid item xs={12}>
          <Alert severity="warning">{i18next.t('publiccompany-inactive-contacts-warning')}</Alert>
        </Grid>
      )}

      {/* Active Contacts */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('publicclient-active-contacts')}
        </Typography>
        <PagedTable
          filterKey={`company-active-${props.data.id}`}
          refetchPage={(skip, take) => {
            refetchActiveClients({
              where: { publicCompanyId: props.data.id, isActive: true },
              skip,
              take,
            });
          }}
          rowsLoading={activeClientsLoading}
          headers={[
            String(i18next.t('publicclient-lastname')),
            String(i18next.t('publicclient-firstname')),
            String(i18next.t('publicclient-responsibility')),
            String(i18next.t('publicclient-email')),
            String(i18next.t('status')),
            String(i18next.t('actions')),
          ]}
          totalCount={activeClientsData?.listPublicClients?.totalCount}
          rows={(activeClientsData?.listPublicClients?.clients || []).map(row => [
            row.lastname || '-',
            row.firstname || '-',
            row.responsibility || '-',
            row.email,
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {row.isMaster && <Chip label={String(i18next.t('publicclient-status-master'))} size="small" color="primary" variant="outlined" />}
              {isInvitationPending(row) && (
                <Chip label={String(i18next.t('publicclient-status-invitation-pending'))} size="small" color="warning" variant="outlined" />
              )}
            </Box>,
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={String(i18next.t('edit'))}>
                <IconButton size="small" color="primary" onClick={() => navigate(`/clients/clients/${row.id}`)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {isInvitationPending(row) && (
                <Tooltip title={String(i18next.t('publicclient-resend-invitation'))}>
                  <IconButton size="small" onClick={() => handleResendInvitation(row.id, `${row.firstname} ${row.lastname}`)}>
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
              )}
              <ConfirmationButton
                confirmationTitle={i18next.t('publicclient-deactivate-title')}
                confirmationQuestion={i18next.t('publicclient-deactivate-question')}
                onConfirm={() => handleDeactivateClient(row.id, `${row.firstname} ${row.lastname}`)}
                icon={true}
                disabled={false}
              >
                <Tooltip title={String(i18next.t('deactivate'))}>
                  <DeleteIcon />
                </Tooltip>
              </ConfirmationButton>
              {props.data.id > 0 && user.isSeminargo && (
                <ConfirmationButton
                  icon={true}
                  confirmationQuestion={i18next.t('publicclient-confirm-sudo')}
                  confirmationTitle={i18next.t('publicclient-confirm-sudo-title')}
                  onConfirm={async () => {
                    try {
                      const res = await sudoClientMutation({
                        variables: { publicClientId: row.id },
                      });
                      if (res.data?.sudoClient.success) {
                        openLinkInNewTab(res.data.sudoClient.href || '');
                      } else {
                        dispatchException(dispatch, `${i18next.t('publicclient-sudo-failed')}`);
                      }
                    } catch (err) {
                      dispatchException(dispatch, err);
                    }
                  }}
                >
                  <LoginIcon />
                </ConfirmationButton>
              )}
            </Box>,
          ])}
        />
      </Grid>

      {/* Inactive Contacts */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('publicclient-inactive-contacts')}
        </Typography>
        <PagedTable
          filterKey={`company-inactive-${props.data.id}`}
          refetchPage={(skip, take) => {
            refetchInactiveClients({
              where: { publicCompanyId: props.data.id, isActive: false },
              skip,
              take,
            });
          }}
          rowsLoading={inactiveClientsLoading}
          headers={[
            String(i18next.t('publicclient-lastname')),
            String(i18next.t('publicclient-firstname')),
            String(i18next.t('publicclient-responsibility')),
            String(i18next.t('publicclient-email')),
            String(i18next.t('status')),
            String(i18next.t('actions')),
          ]}
          totalCount={inactiveClientsData?.listPublicClients?.totalCount}
          rows={(inactiveClientsData?.listPublicClients?.clients || []).map(row => [
            row.lastname || '-',
            row.firstname || '-',
            row.responsibility || '-',
            row.email,
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {row.isMaster && <Chip label={String(i18next.t('publicclient-status-master'))} size="small" color="primary" variant="outlined" />}
              <Chip label={String(i18next.t('publicclient-status-inactive'))} size="small" color="default" variant="outlined" />
            </Box>,
            <ConfirmationButton
              confirmationTitle={i18next.t('publicclient-reactivate-title')}
              confirmationQuestion={i18next.t('publicclient-reactivate-question')}
              onConfirm={() => handleReactivateClient(row.id, `${row.firstname} ${row.lastname}`)}
              icon={true}
              disabled={!isCompanyActive}
            >
              <Tooltip title={String(i18next.t('restore'))}>
                <RestoreIcon />
              </Tooltip>
            </ConfirmationButton>,
          ])}
        />
      </Grid>

      {/* New Contact Button */}
      <Grid item xs={12}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/clients/clients/new?companyId=${props.data.id}`)}
          disabled={!isCompanyActive}
          sx={{ mt: 2 }}
        >
          {i18next.t('publicclient-button-new')}
        </Button>
      </Grid>

      {/* User Groups */}
      <Grid item xs={12}>
        <UserGroupsSection publicCompanyId={props.data.id} companyName={props.data.name || ''} />
      </Grid>
    </Grid>
  );

  // Billing Tab Content
  const billingTab = (
    <Grid container spacing={3}>
      {/* Pricing Settings */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SettingsIcon sx={{ mr: 1 }} />
            {i18next.t('publiccompany-billing-price-display')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormInputCheckbox name="showPricesInNet" control={control} label={String(i18next.t('publiccompany-show-prices-net'))} />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating}
              onClick={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmit)();
                }
              }}
            >
              {i18next.t('publiccompany-billing-save-settings')}
            </Button>
          </Box>
        </Paper>
      </Grid>

      {/* Accounting Areas */}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BusinessIcon sx={{ mr: 1 }} />
            {i18next.t('publiccompany-billing-accounting-areas')}
          </Typography>
          {accountingAreasLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <AccountingAreasComponent accountingAreas={accountingAreasData?.listAccountingAreas || []} publicCompanyId={props.data.id} />
          )}
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([isNew ? i18next.t('publiccompany-new-title') : i18next.t('clients-list-page-title'), props.data])}</title>
      </Helmet>
      <Box sx={{ mb: 2 }}>
        <Button component={Link} to="/clients/companies" startIcon={<ArrowBackIcon />} sx={{ textDecoration: 'none' }}>
          {i18next.t('publiccompany-button-back')}
        </Button>
      </Box>
      {!isNew && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4">{props.data.name}</Typography>
          <Chip
            label={String(i18next.t(isCompanyActive ? 'publiccompany-status-active' : 'publiccompany-status-inactive'))}
            color={isCompanyActive ? 'success' : 'error'}
          />
        </Box>
      )}
      {isNew ? (
        // For new companies, show only the form without tabs
        basicDataTab
      ) : (
        // For existing companies, show all tabs
        <CustomTabs
          headers={[
            i18next.t('publiccompany-tab-basic-data'),
            i18next.t('publiccompany-tab-contracts'),
            i18next.t('publiccompany-tab-contacts'),
            i18next.t('publiccompany-tab-billing'),
          ]}
          slugs={['basic-data', 'contracts', 'contacts', 'billing']}
          tabs={[basicDataTab, contractsTab, contactsTab, billingTab]}
        />
      )}

      {/* Dialog for blocked deactivation */}
      <Dialog open={showBookingsDialog} onClose={() => setShowBookingsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {i18next.t(blockedDialogType === 'company' ? 'publiccompany-blocked-dialog-title' : 'publicclient-blocked-dialog-title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {i18next.t(blockedDialogType === 'company' ? 'publiccompany-blocked-dialog-message' : 'publicclient-blocked-dialog-message')}
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, color: 'error.main' }}>
            {blockingReason}
          </Typography>

          <Typography variant="h6" sx={{ mb: 2 }}>
            {i18next.t('publicclient-blocked-bookings-title')}
          </Typography>

          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            {blockingBookings.map((booking: any, index: number) => (
              <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {i18next.t('publicclient-blocked-booking-id')}: {booking.refCode}
                </Typography>
                {blockedDialogType === 'company' && (
                  <Typography variant="body2" color="text.secondary">
                    {i18next.t('publiccompany-blocked-contact')}: {booking.clientName || '-'}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {booking.title} • {formatDate(new Date(booking.startDate))} -{' '}
                  {formatDate(new Date(booking.endDate))}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBookingsDialog(false)} variant="contained" color="primary" size="small">
            {i18next.t('publicclient-blocked-dialog-close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Company detail page wrapper that loads company data.
 * Renders company form for editing company information, contacts, and comments.
 * Supports creation (id=0) and editing modes.
 */
export default function PublicCompany(props: PublicCompanyProps) {
  const clientQuery = useQuery(VIEW_PUBLIC_COMPANY_QUERY, {
    variables: { id: props.id },
    skip: props.id === 0,
  });

  if (props.id === 0) {
    // New company - provide empty data
    const emptyCompany: HL_PublicCompanyOutput = {
      id: 0,
      name: '',
      mainLanguage: 'DE',
      address: '',
      zip: '',
      city: '',
      country: ECountryCode.AT,
      state: '',
      addressSupplement: '',
      phoneCountryCode: '+43',
      phone: '',
      email: '',
      industry: '',
      website: '',
      keyAccountManagerId: null,
      keyAccountManager: null,
      isPremium: false,
      isActive: true,
      showPricesInNet: true,
      clientsCount: 0,
    };
    return <PublicCompanyForm data={emptyCompany} />;
  }

  if (clientQuery.loading) return <CircularProgress />;
  else if (clientQuery.error) return <Alert severity="error">{clientQuery.error.message}</Alert>;
  else return <PublicCompanyForm data={clientQuery.data!.viewPublicCompany as HL_PublicCompanyOutput} />;
}
